import React, { useState, useEffect, useMemo } from 'react';
import { MessageCircle, Clock } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import StreamChatComponent from '../../messaging/components/StreamChatComponent';

const ChatTab = ({ voProvider }) => {
  const { theme } = useTheme();
  const [members, setMembers] = useState([]);

  // All hooks must be declared unconditionally before any early return
  useEffect(() => {
    if (!voProvider) return;
    const voMembers = voProvider.getMembers();
    if (voMembers?.length) setMembers(voMembers);
  }, [voProvider]);

  useEffect(() => {
    if (!voProvider) return;
    voProvider.setOnStateChange((changeType, data) => {
      if (changeType === 'members') setMembers(data);
    });
    return () => voProvider.setOnStateChange(null);
  }, [voProvider]);

  const acceptedMembers = useMemo(
    () => members.filter(m => m.status === 'active'),
    [members]
  );

  if (!voProvider) {
    return (
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-6 text-center`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  const group = voProvider.getGroup();
  const groupId = voProvider.vo.groupId;
  const isMember = group?.isMember;
  const memberStatus = group?.memberStatus;

  if (isMember && memberStatus !== 'invited' && memberStatus !== 'pending') {
    return (
      <StreamChatComponent
        channelId={groupId}
        channelName={group.name}
        creatorId={group.createdBy}
        members={acceptedMembers}
      />
    );
  }

  if (memberStatus === 'invited') {
    return (
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-6 text-center`}>
        <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className={`font-semibold ${theme.text} mb-2`}>Accept Invitation to Chat</h3>
        <p className={`text-sm ${theme.textSecondary}`}>
          You need to accept the group invitation before you can access the chat
        </p>
      </div>
    );
  }

  if (memberStatus === 'pending') {
    return (
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-6 text-center`}>
        <Clock className="w-12 h-12 text-orange-400 mx-auto mb-3" />
        <h3 className={`font-semibold ${theme.text} mb-2`}>Request Pending</h3>
        <p className={`text-sm ${theme.textSecondary}`}>
          Your join request is awaiting admin approval. You'll be able to chat once approved.
        </p>
      </div>
    );
  }

  return (
    <div className={`${theme.card} rounded-2xl border ${theme.border} p-6 text-center`}>
      <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
      <h3 className={`font-semibold ${theme.text} mb-2`}>Join to Chat</h3>
      <p className={`text-sm ${theme.textSecondary}`}>
        You need to be a member of this group to access the chat
      </p>
    </div>
  );
};

export default ChatTab;
